/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Consolidated post payment utilities
 */
import _ from 'lodash';
import { Form } from '@formily/core';

import { POST_PAYMENT_STATE } from 'app-constants/claims';
import { ADJUSTMENT_GROUP_CODES, PATIENT_RESPONSIBILITIES_GROUP_CODES } from 'app-constants/claims/reasonCodes';
import {
	CLAIM_RESPONSE_SOURCES,
	ENTER_MANUAL_MODE_NO_DATA_MODAL,
	MULTIPLE_PAYMENTS_MODAL,
	POST_PAYMENT_MODES,
	SELECTION_ERROR_MESSAGE,
} from 'app-constants/claims/postPaymentConfig';

const toCents = (amount: number | null | undefined | unknown): number => _.round((_.toNumber(amount) || 0) * 100);

const fromCents = (amountCents: number): number => parseFloat((amountCents / 100).toFixed(2));

const getPendingPRFromClaims = (claims: PostPayment.IPostPaymentClaimModel[] = []): number => {
	const totalPRCents = _.sumBy(claims, (claim) => toCents(claim?.patientResponsibilityAmount));
	return fromCents(totalPRCents);
};

type CodeInput = {
	code?: string;
	system?: string;
	display?: string;
};

type LineAdjustmentLike = {
	group?: { value?: string } | string | null;
	reasonCode?: { value?: string } | string | null;
	amount?: number | null;
};

const hasReasonCodeValue = (reasonCode: LineAdjustmentLike['reasonCode']): boolean => {
	if (!reasonCode) {
		return false;
	}
	if (_.isString(reasonCode)) {
		return reasonCode?.trim().length > 0;
	}

	return !!reasonCode?.value;
};

const sumFields = <T>(items: T[] = [], field: string, filter?: (item: T) => boolean): number => {
	if (!items.length) {
		return 0;
	}

	const filtered = filter ? _.filter(items, filter) : items;
	return _.sumBy(filtered, (item) => toCents((item as Record<string, unknown>)?.[field]));
};

const setValuesInBatch = (form: Form, updates: Record<string, unknown>): void => {
	_.forEach(updates, (value, path) => {
		if (form.getValuesIn(path) !== value) {
			form.setValuesIn(path, value);
		}
	});
};

const normalizeCodes = (
	codeValue: CodeInput | string | (CodeInput | string)[] | null | undefined,
): { code: string; system?: string; display?: string }[] => {
	const toCodeShape = (value: CodeInput | string | null | undefined): { code: string; system?: string; display?: string } | null => {
		if (!value) {
			return null;
		}

		if (_.isString(value)) {
			return { code: value };
		}

		if (_.isPlainObject(value)) {
			const { code, system, display } = value;
			if (code || system || display) {
				return { code: code || '', system, display };
			}
		}

		return null;
	};

	const values = _.isArray(codeValue) ? codeValue : _.compact([codeValue]);
	return _.compact(_.map(values, toCodeShape));
};

const filterEmptyLineAdjustments = <T extends LineAdjustmentLike>(lineItems: T[] = [], allowEmptyAmount: boolean = false): T[] => {
	return _.filter(lineItems, (item) => {
		return (allowEmptyAmount ? true : item?.amount != null && item.amount !== 0) && hasReasonCodeValue(item?.reasonCode);
	});
};

type ClaimSelectionOptions = {
	includeManualClaims?: boolean;
};

// Claims to render in print: 277 status, manual, and all submitted claims (selection is ignored for the printout).
const getPrintableClaims = (values?: PostPayment.IAdjudicationSchema): PostPayment.IPostPaymentClaimModel[] => {
	const manualClaim = values?.manualClaims ? [values.manualClaims] : [];
	const status277Claim = values?.claimStatus277 ? [values.claimStatus277] : [];
	const claims = _.isArray(values?.claims) ? values?.claims || [] : _.compact([values?.claims]);

	return _.compact([...status277Claim, ...manualClaim, ...claims]);
};

const getSelectedClaims = (
	values?: PostPayment.IAdjudicationSchema,
	{ includeManualClaims = true }: ClaimSelectionOptions = {},
): PostPayment.IPostPaymentClaimModel[] => {
	const claims: PostPayment.IPostPaymentClaimModel[] = [];

	if (includeManualClaims && values?.manualClaims && values.manualClaims.isSelected !== false) {
		claims.push(values.manualClaims);
	}

	_.forEach(values?.claims || [], (claim) => {
		if (claim && claim.isSelected !== false) {
			claims.push(claim);
		}
	});

	return claims;
};

const getAdjustmentTotalsCents = (
	lineAdjudications: PostPayment.IAdjudicationLineItem[] = [],
): {
	adjustedCents: number;
	patientResponsibilityCents: number;
	adjudicatedCents: number;
} => {
	return _.reduce(
		lineAdjudications,
		(acc, item) => {
			const groupCode = item?.group?.value;
			const amountCents = toCents(item?.amount);

			acc.adjudicatedCents += amountCents;
			if (_.includes(PATIENT_RESPONSIBILITIES_GROUP_CODES, groupCode)) {
				acc.patientResponsibilityCents += amountCents;
			}
			if (_.includes(ADJUSTMENT_GROUP_CODES, groupCode)) {
				acc.adjustedCents += amountCents;
			}

			return acc;
		},
		{ adjustedCents: 0, patientResponsibilityCents: 0, adjudicatedCents: 0 },
	);
};

const getAdjustmentTotals = (
	lineAdjudications: PostPayment.IAdjudicationLineItem[] = [],
): {
	totalAdjustments: number;
	patientResponsibility: number;
} => {
	const { adjustedCents, patientResponsibilityCents } = getAdjustmentTotalsCents(lineAdjudications);

	return {
		totalAdjustments: fromCents(adjustedCents),
		patientResponsibility: fromCents(patientResponsibilityCents),
	};
};

type FilteredClaimResponses<T> = {
	era: T[];
	manual: T[];
	status277: T[];
};

const MANUAL_CLAIM_RESPONSE_SOURCES = [CLAIM_RESPONSE_SOURCES.user, CLAIM_RESPONSE_SOURCES.post_manual];

const filterClaimResponsesBySource = <T extends { source?: string }>(responses: T[]): FilteredClaimResponses<T> => {
	return {
		era: _.filter(responses, ({ source }) => source === CLAIM_RESPONSE_SOURCES.x12_835),
		manual: _.filter(responses, ({ source }) => _.includes(MANUAL_CLAIM_RESPONSE_SOURCES, source)),
		status277: _.filter(responses, ({ source }) => source === CLAIM_RESPONSE_SOURCES.x12_277),
	};
};

const calculateAdjudicationSummary = (
	lineAdjudications: PostPayment.IAdjudicationLineItem[] = [],
): { totalAdjustments: number; patientResponsibility: number } => {
	return getAdjustmentTotals(lineAdjudications);
};

const validateLineBalances = ({
	formValues,
	billedAmount,
}: {
	formValues: PostPayment.IAdjudicationSchema;
	billedAmount: number | null;
}): PostPayment.IValidateLineBalances => {
	const failedLinesIndex: number[] = [];
	const lineAdjustmentFailedLinesIndex: number[] = [];
	let hasEmptyLines = false;
	let hasLineAdjustmentMismatch = false;
	let isSummaryValid = true;
	let globalLineIndex = 0;
	let isTotalBilledValid = true;

	const selectedClaims = getSelectedClaims(formValues);

	if (_.isEmpty(selectedClaims)) {
		return {
			isValid: false,
			failedLinesIndex,
			isSummaryValid: false,
			hasEmptyLines: true,
			hasLineAdjustmentMismatch,
			lineAdjustmentFailedLinesIndex,
			isTotalPaymentValid: false,
			isTotalBilledValid: false,
		};
	}

	_.forEach(selectedClaims, (claim) => {
		const serviceLines = claim?.serviceLines || [];

		_.forEach(serviceLines, (line) => {
			const lineTotals = {
				billed: toCents(line.billedAmount),
				allowed: toCents(line.allowedAmount),
				adjusted: toCents(line.adjustedAmount),
				paid: toCents(line.paidAmount),
				patientResponsibility: toCents(line.patientResponsibilityAmount),
			};

			const lineAdjudications = line?.lineAdjudication || [];
			const { totalAdjustments, patientResponsibility } = calculateAdjudicationSummary(lineAdjudications);
			const adjudicationTotals = {
				adjusted: toCents(totalAdjustments),
				patientResponsibility: toCents(patientResponsibility),
			};
			const hasLineAdjudications = !_.isEmpty(lineAdjudications);
			if (
				hasLineAdjudications &&
				(adjudicationTotals.adjusted !== lineTotals.adjusted ||
					adjudicationTotals.patientResponsibility !== lineTotals.patientResponsibility)
			) {
				hasLineAdjustmentMismatch = true;
				lineAdjustmentFailedLinesIndex.push(globalLineIndex);
			}

			const isZeroRow = _.every(_.values(lineTotals), (value) => value === 0);
			const isBilledBalanced = lineTotals.allowed + lineTotals.adjusted - lineTotals.billed === 0;
			const isAllowedBalanced = lineTotals.paid + lineTotals.patientResponsibility - lineTotals.allowed === 0;

			if (isZeroRow && (!isBilledBalanced || !isAllowedBalanced)) {
				hasEmptyLines = true;
			} else if (!isBilledBalanced || !isAllowedBalanced) {
				failedLinesIndex.push(globalLineIndex);
			}

			globalLineIndex += 1;
		});

		const pBilled = toCents(claim?.billedAmount);
		const pPaid = toCents(claim?.paidAmount);
		const pPatResp = toCents(claim?.patientResponsibilityAmount);
		const pAdj = toCents(claim?.adjustedAmount);
		isSummaryValid = isSummaryValid && pBilled === pPaid + pPatResp + pAdj;
	});

	const totalBilledAmount = _.sumBy(selectedClaims, (claim) => toCents(claim?.billedAmount));
	isTotalBilledValid = totalBilledAmount === toCents(billedAmount);

	return {
		isValid: _.isEmpty(failedLinesIndex) && isSummaryValid && !hasLineAdjustmentMismatch && !hasEmptyLines && isTotalBilledValid,
		failedLinesIndex,
		isSummaryValid,
		hasEmptyLines,
		hasLineAdjustmentMismatch,
		lineAdjustmentFailedLinesIndex,
		isTotalPaymentValid: isSummaryValid,
		isTotalBilledValid,
	};
};

type StateTransitionFn = () => PostPayment.IPostPaymentStateAction;

const STATE_TRANSITIONS: Record<string, StateTransitionFn> = {
	[POST_PAYMENT_STATE.draftClaim]: () => ({
		type: 'enterManualMode',
	}),
	[POST_PAYMENT_STATE.manualPosted]: () => ({
		type: 'enterManualMode',
	}),
	[POST_PAYMENT_STATE.multiplePayments]: () => ({
		type: 'showConfirmModal',
		modalConfig: MULTIPLE_PAYMENTS_MODAL,
		onConfirm: 'enterSelectionMode',
	}),
	[POST_PAYMENT_STATE.pendingEra]: () => ({
		type: 'showConfirmModal',
		modalConfig: ENTER_MANUAL_MODE_NO_DATA_MODAL,
		onConfirm: 'enterManualMode',
	}),
	[POST_PAYMENT_STATE.emptyClaim]: () => ({
		type: 'showConfirmModal',
		modalConfig: ENTER_MANUAL_MODE_NO_DATA_MODAL,
		onConfirm: 'enterManualMode',
	}),
	[POST_PAYMENT_STATE.autoPosted]: () => ({
		type: 'noop',
	}),
	[POST_PAYMENT_STATE.closedClaim]: () => ({
		type: 'noop',
	}),
};

const getStateAction = (state: ClaimStatusResponse.IPostPaymentState): PostPayment.IPostPaymentStateAction => {
	const transition = STATE_TRANSITIONS[state];
	if (transition) {
		return transition();
	}

	return { type: 'openConfirmationModal' };
};

const getPostPaymentDecision = ({
	mode,
	postPaymentState,
	hasSelectedClaims,
}: {
	mode: PostPayment.IPostPaymentMode;
	postPaymentState: ClaimStatusResponse.IPostPaymentState;
	hasSelectedClaims: boolean;
}): PostPayment.IPostPaymentDecision => {
	if (mode === POST_PAYMENT_MODES.manual) {
		return {
			type: 'validateAndOpen',
			isAutoPosting: false,
			isManualPosted: postPaymentState === POST_PAYMENT_STATE.manualPosted,
		};
	}

	if (mode === POST_PAYMENT_MODES.select) {
		if (!hasSelectedClaims) {
			return {
				type: 'selectionError',
				message: SELECTION_ERROR_MESSAGE,
			};
		}

		return {
			type: 'validateAndOpen',
			isAutoPosting: true,
		};
	}

	return {
		type: 'stateAction',
		action: getStateAction(postPaymentState),
	};
};

const validatePostPaymentSummary = async ({
	claimId,
	getOperations,
	getPaymentSummary,
	getFormPaymentSummary,
	includeManualClaimData = false,
}: {
	claimId: string;
	getOperations: PostPayment.IGetOperationsFn;
	getPaymentSummary: PostPayment.IGetPaymentSummaryFn;
	getFormPaymentSummary: () => PostPayment.IPaymentSummaryTotals | undefined;
	includeManualClaimData?: boolean;
}): Promise<PostPayment.IPostPaymentSummaryValidationResult | null> => {
	const operations = getOperations({ includeManualClaimData });
	const summary = await getPaymentSummary({ claimId, operations }).catch(() => null);
	if (!summary) {
		return null;
	}

	const metadata = summary?.metadata;
	const billedCents = toCents(metadata?.billed);
	const payerPaidCents = toCents(metadata?.payerPaid);
	const patientResponsibilityCents = toCents(metadata?.patientResponsibility);
	const payerAdjustedCents = toCents(metadata?.payerAdjusted);
	const writeOffCents = toCents(metadata?.writeOff);
	const billedDiffCents = toCents(metadata?.billedDiff);
	const balanceDiffCents = billedCents - payerPaidCents - patientResponsibilityCents - payerAdjustedCents - writeOffCents;

	const formSummary = getFormPaymentSummary();
	const formBilledCents = toCents(formSummary?.billedAmount);
	const formPaidCents = toCents(formSummary?.paidAmount);
	const formPRCents = toCents(formSummary?.patientResponsibilityAmount);
	const formAdjustedCents = toCents(formSummary?.adjustedAmount);
	const hasFormMismatch =
		formBilledCents !== billedCents ||
		formPaidCents !== payerPaidCents ||
		formPRCents !== patientResponsibilityCents ||
		formAdjustedCents !== payerAdjustedCents;

	return {
		hasMismatch: billedDiffCents !== 0 || balanceDiffCents !== 0 || hasFormMismatch,
		pendingPR: Number(metadata?.patientResponsibility) || 0,
	};
};

export {
	toCents,
	fromCents,
	getPendingPRFromClaims,
	sumFields,
	setValuesInBatch,
	normalizeCodes,
	filterEmptyLineAdjustments,
	getPrintableClaims,
	getSelectedClaims,
	getAdjustmentTotalsCents,
	getAdjustmentTotals,
	filterClaimResponsesBySource,
	calculateAdjudicationSummary,
	validateLineBalances,
	getPostPaymentDecision,
	validatePostPaymentSummary,
};
